import React from 'react';
import { motion } from 'framer-motion';
import { Play, MessageCircle, BookOpen, Library, FileText, Gift, CheckCircle2, ArrowRight } from 'lucide-react';
import { getCheckoutUrl } from '../utils/utm';

export const OfferBreakdown = () => {
  const checkoutUrl = getCheckoutUrl();

  const allItems = [
    {
      icon: Play,
      title: "Charles Payne's Trade Lab",
      headline: "Watch Charles's Framework Applied to Real Trades, Twice Every Month",
      description: "In live Trade Lab sessions, Charles's team walks through actual trade ideas and explains the reasoning behind every decision. You see the three-pillar framework applied to real market conditions, not textbook hypotheticals. You learn not just what was selected but exactly why, what criteria it met, and what would change the thesis. After your first few Trade Lab sessions, you will think about markets differently.",
      testimonial: { quote: "I understand how to analyze a trade now, not just follow one." },
      value: "$97/month",
      badge: "2x Monthly Live Sessions",
      featured: true
    },
    {
      icon: MessageCircle,
      title: "Live Weekly Q and A Sessions",
      headline: "Every Question You Have Gets a Real Answer, Every Week",
      description: "The most common experience in investing education is finishing a lesson with three new questions and nowhere to take them. Every week, members join a live session with Charles's instructors and ask anything. A stock you are watching. A sector you do not understand. A concept from the foundation lessons. Something you saw in the news. No question is too basic. No question is off-limits. All sessions are recorded so you can revisit any answer in the member library.",
      testimonial: { quote: "I finally have somewhere to bring my questions and get an honest answer." },
      value: "$79/month",
      badge: "Weekly Live Sessions"
    },
    {
      icon: BookOpen,
      title: "Stock Investing Foundation Lessons",
      headline: "The Core Curriculum That Builds Your Analytical Foundation From the Ground Up",
      description: "This is the structured learning backbone of the program. The Foundation Lessons cover how to evaluate a company's financial position, how to read sector dynamics, how to apply the three-pillar approach to opportunity identification, and how to construct a portfolio with a clear internal logic. Built for investors at every experience level. Goes deep enough for experienced investors who want to replace intuition with system.",
      testimonial: { quote: "Education is everything. My results have been transformed." },
      value: "$197",
      badge: "Complete Curriculum"
    },
    {
      icon: Library,
      title: "Beginner to Intermediate Video Series",
      headline: "Go Deeper at Your Own Pace With Structured On-Demand Learning",
      description: "Once you have your foundational framework, this series takes your knowledge further with more detailed instruction on specific analysis techniques, sector dynamics, and portfolio management concepts. All on demand. Work through it at whatever pace fits your schedule. Revisit any session as many times as you need.",
      value: "$147",
      badge: "On-Demand Access"
    },
    {
      icon: FileText,
      title: "Charles's Bestselling Books (Digital)",
      headline: "The Full Investing Philosophy Behind the Program, In Charles's Own Words",
      description: "Unstoppable Prosperity covers Charles's foundational approach to identifying opportunity and building long-term wealth through disciplined, sector-aware investing. Unbreakable Investor addresses the behavioral and psychological dimension, why investors undermine their own results and how to stop. Together they provide the complete intellectual foundation that every lesson inside Payne's Education is built on.",
      value: "$39",
      badge: "Instant Download"
    },
    {
      icon: FileText,
      title: "Stock Investing Terminology Guide",
      headline: "Never Feel Lost in the Language of Investing Again",
      description: "One of the most underestimated barriers to confident investing is vocabulary. When a Trade Lab session, an earnings report, or a market commentary uses a term you do not recognize, you lose the thread of the analysis. This guide gives you a plain-language reference for every key term you will encounter, designed to be used as a quick lookup or as a structured read-through.",
      value: "$29",
      badge: "Quick Reference"
    }
  ];

  const bonus = {
    icon: Gift,
    title: "Tax Strategies for Investors",
    headline: "This Bonus Training Is Yours to Keep Even If You Cancel Your Trial",
    description: "Tax Strategies for Investors is a premium video training covering how tax planning integrates with active portfolio management. How holding periods affect your tax exposure. How to think about account structure and its long-term compounding implications. How smart tax decisions over time accumulate into meaningful additional wealth. Members who have applied these strategies have collectively reclaimed millions in unnecessary tax payments. This training is yours permanently, whether you continue your membership or not.",
    value: "$97",
    badge: "Exclusive Bonus: Claim at No Additional Cost"
  };

  const totalValue = 685;

  return (
    <section 
      data-testid="offer-breakdown"
      id="offer-details"
      className="py-20 md:py-28 px-8 md:px-12 bg-[#0A1628] text-white"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[2px] bg-amber-600" />
            <p className="font-sans text-xs tracking-widest uppercase text-amber-500">
              What You Get
            </p>
            <div className="w-12 h-[2px] bg-amber-600" />
          </div>
          
          <h2 className="font-editorial text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            Here Is Exactly What You Get Access to{' '}
            <span className="text-amber-500">the Moment You Join</span>
          </h2>
          <p className="font-sans text-lg text-gray-400 max-w-2xl mx-auto">
            Every one of these resources is available to you from day one.
          </p>
        </motion.div>

        {/* All Items - Same Size */}
        <div className="space-y-6 mb-6">
          {allItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              data-testid={`offer-component-${index}`}
              className={`p-5 md:p-10 border ${item.featured 
                ? 'bg-gradient-to-r from-amber-600/20 to-amber-600/5 border-amber-600/30' 
                : 'bg-white/5 border-white/10 hover:border-amber-600/50'} transition-colors`}
            >
              {/* Desktop: horizontal layout */}
              <div className="hidden md:block">
                <div className="flex items-start gap-6">
                  <div className={`w-16 h-16 flex items-center justify-center flex-shrink-0 ${item.featured ? 'bg-amber-600' : 'bg-white/10'}`}>
                    <item.icon className={`w-8 h-8 ${item.featured ? 'text-white' : 'text-amber-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-editorial text-2xl font-bold text-white">
                        {item.title}
                      </h3>
                      <span className={`font-sans text-xs tracking-wider uppercase px-3 py-1 ${item.featured ? 'text-amber-500 bg-amber-600/20' : 'text-amber-400 bg-amber-600/10'}`}>
                        {item.badge}
                      </span>
                    </div>
                    <p className="font-sans text-amber-400 font-medium text-sm mb-3">{item.headline}</p>
                    <p className="font-sans text-gray-300 leading-relaxed text-base">
                      {item.description}
                    </p>
                    {item.testimonial && (
                      <p className="font-sans text-gray-400 italic text-sm mt-4 border-l-2 border-amber-600/40 pl-4">
                        "{item.testimonial.quote}"
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <span className="font-editorial text-3xl font-bold text-amber-500">Value: {item.value}</span>
                </div>
              </div>

              {/* Mobile: centered vertical layout */}
              <div className="md:hidden flex flex-col items-center text-center">
                <div className={`w-14 h-14 flex items-center justify-center mb-4 ${item.featured ? 'bg-amber-600' : 'bg-white/10'}`}>
                  <item.icon className={`w-7 h-7 ${item.featured ? 'text-white' : 'text-amber-500'}`} />
                </div>
                <h3 className="font-editorial text-xl font-bold text-white mb-2">
                  {item.title}
                </h3>
                <span className={`font-sans text-xs tracking-wider uppercase px-3 py-1 mb-3 ${item.featured ? 'text-amber-500 bg-amber-600/20' : 'text-amber-400 bg-amber-600/10'}`}>
                  {item.badge}
                </span>
                <p className="font-sans text-amber-400 font-medium text-sm mb-3">{item.headline}</p>
                <p className="font-sans text-gray-300 leading-relaxed text-sm mb-4">
                  {item.description}
                </p>
                {item.testimonial && (
                  <p className="font-sans text-gray-400 italic text-xs mb-4">
                    "{item.testimonial.quote}"
                  </p>
                )}
                <span className="font-editorial text-xl font-bold text-amber-500">Value: {item.value}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bonus - Highlighted */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative mb-12"
        >
          {/* Bonus Badge */}
          <div className="absolute -top-4 left-8 z-10">
            <span className="bg-amber-600 text-white font-sans font-bold text-sm px-4 py-2 uppercase tracking-wider">
              Exclusive Bonus: Claim at No Additional Cost
            </span>
          </div>
          
          <div className="bg-gradient-to-r from-amber-600/30 to-amber-500/10 border-2 border-amber-600 p-5 md:p-10 pt-10 md:pt-12">
            {/* Desktop: horizontal layout */}
            <div className="hidden md:block">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-amber-600 flex items-center justify-center flex-shrink-0">
                  <bonus.icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h3 className="font-editorial text-2xl font-bold text-white">
                      {bonus.title}
                    </h3>
                  </div>
                  <p className="font-sans text-amber-400 font-medium text-sm mb-3">{bonus.headline}</p>
                  <p className="font-sans text-gray-300 leading-relaxed text-base mb-4">
                    {bonus.description}
                  </p>
                  <p className="font-sans text-amber-400 font-medium flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Yours to keep even if you cancel your trial
                  </p>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <span className="font-editorial text-3xl font-bold text-amber-500">Value: {bonus.value}</span>
              </div>
            </div>

            {/* Mobile: centered vertical layout */}
            <div className="md:hidden flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-amber-600 flex items-center justify-center mb-4">
                <bonus.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-editorial text-xl font-bold text-white mb-2">
                {bonus.title}
              </h3>
              <p className="font-sans text-amber-400 font-medium text-sm mb-3">{bonus.headline}</p>
              <p className="font-sans text-gray-300 leading-relaxed text-sm mb-4">
                {bonus.description}
              </p>
              <p className="font-sans text-amber-400 font-medium flex items-center gap-2 text-sm mb-4">
                <CheckCircle2 className="w-4 h-4" />
                Yours to keep even if you cancel your trial
              </p>
              <span className="font-editorial text-xl font-bold text-amber-500">Value: {bonus.value}</span>
            </div>
          </div>
        </motion.div>

        {/* Value Summary - Compelling Design */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="relative overflow-hidden"
        >
          {/* Main section - Price Comparison */}
          <div className="bg-white p-8 md:p-12">
            <div className="max-w-xl mx-auto">
              {/* Label above price */}
              <p className="font-sans text-xs tracking-widest uppercase text-gray-400 text-center mb-6">Your investment today</p>

              {/* Price comparison */}
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mb-8">
                <div className="text-center">
                  <p className="font-sans text-xs tracking-widest uppercase text-gray-400 mb-2">Total Value</p>
                  <p className="font-editorial text-5xl md:text-6xl font-bold text-gray-300 line-through">${totalValue}</p>
                </div>
                
                <div className="hidden md:block">
                  <ArrowRight className="w-8 h-8 text-amber-600" />
                </div>
                <div className="md:hidden">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                
                <div className="text-center">
                  <p className="font-sans text-xs tracking-widest uppercase text-amber-600 mb-2">Your Price Today</p>
                  <p className="font-editorial text-6xl md:text-8xl font-bold text-amber-600">$7</p>
                </div>
              </div>

              {/* Price context copy */}
              <p className="font-sans text-sm text-gray-600 text-center mb-8">
                For 30 days of complete access to everything listed above. After your trial, membership continues at $99 per month. Cancel anytime before your trial ends and pay nothing more.
              </p>

              {/* CTA Button */}
              <div className="text-center">
                <a
                  href={checkoutUrl}
                  data-testid="value-stack-cta"
                  className="group inline-flex items-center justify-center gap-3 bg-amber-600 hover:bg-amber-500 text-white font-sans font-bold py-4 md:py-5 px-6 md:px-16 text-sm md:text-base tracking-wider uppercase text-center transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-amber-600/20 w-full md:w-auto"
                >
                  <span>Start My 30-Day Trial for $7</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
                
                <p className="font-sans text-sm text-gray-500 mt-4">
                  100% satisfaction guarantee. Full $7 refund if you cancel during the trial period. Tax Strategies for Investors bonus is yours to keep regardless.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom section - Guarantee strip */}
          <div className="bg-gray-100 p-6">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="font-sans text-sm font-medium text-gray-700">100% Money-Back Guarantee</span>
              </div>
              <div className="hidden md:flex items-center gap-8">
                <div className="w-px h-6 bg-gray-300" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <span className="font-sans text-sm font-medium text-gray-700">Secure Checkout</span>
                </div>
                <div className="w-px h-6 bg-gray-300" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <span className="font-sans text-sm font-medium text-gray-700">Cancel Anytime</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
